import React from 'react';

import {Heading, IconButton, LoadingSpinner} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {ClassroomManagement} from './sections/ClassroomManagement';
import {ClassworkActions} from './sections/ClassworkActions';
import {HomeBanners} from './banners/HomeBanners';
import {JustExploringSection} from './sections/JustExploringSection';
import {WelcomeCard} from './sections/WelcomeCard';

const getGreeting = () => {
  const hour = systemTimeOffsetStore.getCurrentTime().hour();
  if (hour < 12) {
    return 'Good Morning';
  }
  if (hour < 17) {
    return 'Good Afternoon';
  }
  return 'Good Evening';
};

interface Props {
  /** Whether the teacher has any active (non-archived) classes — selects the Home state. */
  hasActiveClasses: boolean;
  /** True until the active-classroom fetch first settles — gates the initial load only, not refetches. */
  isInitialLoad: boolean;
  /** True when the Get Started rail is dismissed — Home then shows the reopen affordance (has-classes only). */
  getStartedCollapsed: boolean;
  onReopenGetStarted: () => void;
}

export const Home = ({
  hasActiveClasses,
  isInitialLoad,
  getStartedCollapsed,
  onReopenGetStarted
}: Props) => {
  const isMasquerading = masqueradeStore.isMasquerading();
  const userId = masqueradeStore.getUserIdByMasqueradeState() ?? '';
  // While masquerading, read the display name from teacher_v1 (admin-readable) instead of users_v2,
  const greetingQuery = (isMasquerading
    ? resource('teachers_v1').mandarkEndpoint(['teachers_v1', userId])
    : getBaseUserQuery(userId)) as unknown as QueryBuilder;
  const {isReady} = useAreLatestQueriesReady([() => greetingQuery]);
  const greetingResource = isReady
    ? (greetingQuery.getResource() as {getFirstName?: () => string} | null)
    : null;
  const firstName = greetingResource?.getFirstName?.() ?? '';

  const renderContent = () => {
    if (isInitialLoad) {
      return <LoadingSpinner assistiveText='Loading your classes…' size='lg' />;
    }
    if (hasActiveClasses) {
      return (
        <>
          <ClassworkActions />
          <ClassroomManagement />
        </>
      );
    }
    return (
      <div className='gap-x5 mx-auto flex w-full max-w-[1000px] flex-col'>
        <WelcomeCard />
        <JustExploringSection />
      </div>
    );
  };

  return (
    <div className='gap-x3 pb-x20 flex flex-col'>
      <div className='mb-x3 gap-x2 flex items-center justify-between'>
        <Heading as='h1' className='m-x0 text-[32px]!' color='primary'>
          {getGreeting()}
          {firstName ? `, ${firstName}` : ''}!
        </Heading>
        {hasActiveClasses && getStartedCollapsed ? (
          <IconButton
            color='secondary'
            icon={['fak', 'regular-square-list-circle-question' as IconName]}
            label='Show Helpful Links'
            onClick={onReopenGetStarted}
            tooltip='Show Helpful Links'
            variant='text'
          />
        ) : null}
      </div>
      <HomeBanners />
      {renderContent()}
    </div>
  );
};
